/**
 * Where your money goes.
 *
 * One form, two places: the onboarding step, and the prompt at redeem for
 * everybody who joined before this existed or skipped it on the way in.
 * Keeping it in one place is the point — the list of fields a bank transfer
 * needs is not something to maintain twice.
 *
 * The account number is write-only. What comes back from the API is the
 * last four digits, so re-opening the form with details already on file
 * shows which account is on it and asks for the number again rather than
 * pretending to hold something it will not show.
 */
import { useState } from "react";
import type { FormEvent } from "react";
import { CountrySelect } from "./CountrySelect";
import { onboarding as onboardingApi } from "../api/endpoints";
import { useConfig } from "../store/config";
import { validateEmail } from "../lib/validation";
import type { PayoutDetails, PayoutDetailsPayload, PayoutDetailsState } from "../types/api";

interface Props {
  /** What is on file, so the form opens on it rather than empty. */
  existing?: PayoutDetails | null;
  /** The contributor's own country, used as the default for the bank's. */
  defaultCountry?: string | null;
  submitLabel?: string;
  onSaved: (state: PayoutDetailsState) => void;
  /** Rendered beside the submit button — "Skip for now", "Cancel". */
  secondary?: React.ReactNode;
}

type Field =
  | "full_legal_name"
  | "country"
  | "bank_name"
  | "account_number"
  | "beneficiary_address"
  | "currency"
  | "email";

const MAX = {
  full_legal_name: 120,
  bank_name: 120,
  account_number: 40,
  beneficiary_address: 300,
  bank_address: 300,
};

export function PayoutDetailsForm({
  existing,
  defaultCountry,
  submitLabel = "Save details",
  onSaved,
  secondary,
}: Props) {
  const currencies = useConfig((state) => state.config.payout.currencies);

  const [fullLegalName, setFullLegalName] = useState(existing?.full_legal_name ?? "");
  const [country, setCountry] = useState(existing?.country ?? defaultCountry ?? "");
  const [bankName, setBankName] = useState(existing?.bank_name ?? "");
  const [accountNumber, setAccountNumber] = useState("");
  const [beneficiaryAddress, setBeneficiaryAddress] = useState(
    existing?.beneficiary_address ?? "",
  );
  const [currency, setCurrency] = useState(existing?.currency ?? "");
  const [bankAddress, setBankAddress] = useState(existing?.bank_address ?? "");
  const [email, setEmail] = useState(existing?.email ?? "");

  const [touched, setTouched] = useState<Partial<Record<Field, boolean>>>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Mirrors what the API enforces, so the form never submits something the
  // API will only then refuse.
  const errors: Partial<Record<Field, string | null>> = {
    full_legal_name: fullLegalName.trim().length < 2 ? "Enter the name on the account" : null,
    country: country ? null : "Choose the bank's country",
    bank_name: bankName.trim().length < 2 ? "Enter your bank's name" : null,
    account_number:
      accountNumber.replace(/[^a-zA-Z0-9]/g, "").length < 4
        ? "Enter your account number or IBAN"
        : null,
    beneficiary_address:
      beneficiaryAddress.trim().length < 5 ? "Enter your address" : null,
    currency: currency ? null : "Choose a currency",
    email: email.trim() ? validateEmail(email) : null,
  };
  const valid = Object.values(errors).every((value) => !value);

  function cls(field: Field): string {
    return touched[field] && errors[field] ? "field err" : "field";
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setTouched({
      full_legal_name: true, country: true, bank_name: true,
      account_number: true, beneficiary_address: true, currency: true, email: true,
    });
    setError(null);
    if (!valid) return;

    setSaving(true);
    try {
      const payload: PayoutDetailsPayload = {
        full_legal_name: fullLegalName.trim(),
        country,
        bank_name: bankName.trim(),
        account_number: accountNumber.trim(),
        beneficiary_address: beneficiaryAddress.trim(),
        currency,
      };
      if (bankAddress.trim()) payload.bank_address = bankAddress.trim();
      if (email.trim()) payload.email = email.trim();

      const saved = await onboardingApi.savePayoutDetails(payload);
      // Not kept in state a moment longer than it has to be.
      setAccountNumber("");
      onSaved(saved);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  }

  // `payout-form` pins the alignment: dropped into the Earnings balance
  // card it would otherwise inherit that card's centring, and a form with
  // centred labels reads as a poster.
  return (
    <form className="panel payout-form" onSubmit={handleSubmit} noValidate>
      <div className={cls("full_legal_name")}>
        <label htmlFor="payout-name">Full legal name</label>
        <input
          id="payout-name"
          value={fullLegalName}
          maxLength={MAX.full_legal_name}
          placeholder="As it appears on your bank account"
          autoComplete="name"
          onChange={(event) => setFullLegalName(event.target.value)}
          onBlur={() => setTouched((t) => ({ ...t, full_legal_name: true }))}
        />
        <div className="hint">
          It has to match the account exactly, or the transfer is rejected.
        </div>
        <div className="err-msg">{touched.full_legal_name ? errors.full_legal_name : ""}</div>
      </div>

      <div className={cls("country")}>
        <label htmlFor="country">Bank country</label>
        <CountrySelect value={country} onChange={setCountry} />
        <div className="hint">Where the account is held, which need not be where you live.</div>
        <div className="err-msg">{touched.country ? errors.country : ""}</div>
      </div>

      <div className={cls("bank_name")}>
        <label htmlFor="payout-bank">Bank name</label>
        <input
          id="payout-bank"
          value={bankName}
          maxLength={MAX.bank_name}
          placeholder="Your bank"
          onChange={(event) => setBankName(event.target.value)}
          onBlur={() => setTouched((t) => ({ ...t, bank_name: true }))}
        />
        <div className="err-msg">{touched.bank_name ? errors.bank_name : ""}</div>
      </div>

      <div className={cls("account_number")}>
        <label htmlFor="payout-account">Account number or IBAN</label>
        <input
          id="payout-account"
          value={accountNumber}
          maxLength={MAX.account_number}
          placeholder={
            existing?.account_number_last4
              ? `Currently ending ${existing.account_number_last4}`
              : "Your account number"
          }
          autoComplete="off"
          spellCheck={false}
          onChange={(event) => setAccountNumber(event.target.value)}
          onBlur={() => setTouched((t) => ({ ...t, account_number: true }))}
        />
        <div className="hint">
          {existing?.account_number_last4
            ? `We hold the account ending ${existing.account_number_last4}. Enter the full number again to change it.`
            : "Stored encrypted. Only the last four digits are ever shown back to you."}
        </div>
        <div className="err-msg">{touched.account_number ? errors.account_number : ""}</div>
      </div>

      <div className={cls("currency")}>
        <label htmlFor="payout-currency">Currency</label>
        <select
          id="payout-currency"
          value={currency}
          onChange={(event) => setCurrency(event.target.value)}
          onBlur={() => setTouched((t) => ({ ...t, currency: true }))}
        >
          <option value="">Choose a currency…</option>
          {currencies.map((code) => (
            <option key={code} value={code}>
              {code}
            </option>
          ))}
        </select>
        <div className="hint">What your account is held in.</div>
        <div className="err-msg">{touched.currency ? errors.currency : ""}</div>
      </div>

      <div className={cls("beneficiary_address")}>
        <label htmlFor="payout-address">Your address</label>
        <input
          id="payout-address"
          value={beneficiaryAddress}
          maxLength={MAX.beneficiary_address}
          placeholder="Street, city, postcode"
          autoComplete="street-address"
          onChange={(event) => setBeneficiaryAddress(event.target.value)}
          onBlur={() => setTouched((t) => ({ ...t, beneficiary_address: true }))}
        />
        <div className="err-msg">
          {touched.beneficiary_address ? errors.beneficiary_address : ""}
        </div>
      </div>

      <div className="field">
        <label htmlFor="payout-bank-address">Bank or branch address (optional)</label>
        <input
          id="payout-bank-address"
          value={bankAddress}
          maxLength={MAX.bank_address}
          placeholder="Only some banks need this"
          onChange={(event) => setBankAddress(event.target.value)}
        />
      </div>

      <div className={cls("email")}>
        <label htmlFor="payout-email">Wise email (optional)</label>
        <input
          id="payout-email"
          type="email"
          value={email}
          placeholder="you@email.com"
          autoComplete="email"
          onChange={(event) => setEmail(event.target.value)}
          onBlur={() => setTouched((t) => ({ ...t, email: true }))}
        />
        <div className="hint">If you already have a Wise account, this speeds things up.</div>
        <div className="err-msg">{touched.email ? errors.email : ""}</div>
      </div>

      {error && <div className="form-error">{error}</div>}

      <div className="btn-row mt-16 payment-save-cancel-row">
        {secondary}
        <button type="submit" className="btn btn-primary" disabled={saving}>
          {saving ? "Saving…" : submitLabel}
        </button>
      </div>
    </form>
  );
}
